import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './components/Sidebar';
import SelectorMarca from '../modules/campana-nueva/components/SelectorMarca';
import logoPrimary from '../assets/logo-primary.png';
import DemoBanner from './components/DemoBanner';

import LogicDetailModal from './components/LogicDetailModal';
import InboxBell from './components/inbox/InboxBell';
import InboxDrawer from './components/inbox/InboxDrawer';
import AssistantBubble from './components/assistant/AssistantBubble';
import AssistantPanel from './components/assistant/AssistantPanel';
import { useInbox } from '../hooks/useInbox';
import { useAssistant } from '../hooks/useAssistant';
import { HOME, NOMBRE_PRODUCTO, SOLO_ACTIVA } from '../config/producto';
import { CuentaOficialProvider } from '../modules/wa-oficial/contexto';
import { R_EMAILS } from '../modules/emails/logic/emails';
import { SECCIONES_EMAILS } from '../modules/emails/SeccionesEmails';
import { ErrorBoundary, ErrorFallback } from '../shared/components/ErrorBoundary';

/**
 * H07 — título dinámico por ruta.
 *
 * Los de /activa siguen al menú: WhatsApp → «Oficial (API)» («· WhatsApp oficial») y «QR (líneas)» («· WhatsApp QR», lo
 * que era «Reactivación»), Emails («· Emails», con el label de `SECCIONES_EMAILS`) y Público. Lo que ninguna regla
 * conoce dice el nombre del producto (`NOMBRE_PRODUCTO`), no «Deenex AI», que era la plataforma anterior.
 * `search` sólo importa en Configuración, donde `?seccion=saldo` es «Saldo».
 */
export function getTitleForPath(pathname: string, search = ""): string {
    // «/activa/emails/» es la misma pantalla que «/activa/emails» para React Router: que diga lo mismo.
    if (pathname.length > 1) pathname = pathname.replace(/\/+$/, "");
    if (pathname === "/dashboard") return "Dashboard · Deenex AI";
    // WhatsApp oficial: las reglas más específicas primero («plantillas/nueva» antes que «plantillas»)
    if (pathname === "/activa/oficial") return "WhatsApp oficial · DEENEX Activa";
    if (pathname.startsWith("/activa/oficial/conversaciones")) return "Conversaciones · WhatsApp oficial";
    if (pathname.startsWith("/activa/oficial/plantillas/nueva")) return "Plantilla nueva · WhatsApp oficial";
    if (pathname.startsWith("/activa/oficial/plantillas")) return "Plantillas · WhatsApp oficial";
    if (pathname.startsWith("/activa/oficial/campanas/nueva")) return "Campaña nueva · WhatsApp oficial";
    if (pathname === "/activa/oficial/campanas/comparar") return "Comparar campañas · WhatsApp oficial";
    if (pathname.startsWith("/activa/oficial/campanas")) return "Campañas · WhatsApp oficial";
    if (pathname === "/activa/oficial/base" || pathname.startsWith("/activa/oficial/base/")) return "Base de datos · WhatsApp oficial";
    if (pathname === "/activa/oficial/estadisticas") return "Estadísticas · WhatsApp oficial";
    if (pathname === "/activa/oficial/calentamiento") return "Calentamiento · WhatsApp oficial";
    if (pathname === "/activa/oficial/imagenes") return "Imágenes · WhatsApp oficial";
    if (pathname === "/activa/oficial/exclusiones") return "Listas de exclusión · WhatsApp oficial";
    if (pathname === "/activa/oficial/disparadores") return "Disparadores · WhatsApp oficial";
    // «/activa/no-contactar» redirige acá: una sola lista de bajas para toda la marca.
    if (pathname === "/activa/oficial/bajas" || pathname === "/activa/no-contactar") return "Bajas y consentimientos · WhatsApp oficial";
    if (pathname === "/activa/oficial/configuracion") {
        // Una persona de un cliente no configura nada: en el menú se llama «Saldo» (`SECCION_SALDO_CLIENTE`).
        return new URLSearchParams(search).get("seccion") === "saldo" ? "Saldo · WhatsApp oficial" : "Configuración · WhatsApp oficial";
    }
    // Campañas: la lista única de los cuatro mundos (el ítem «Campañas» del menú)
    if (pathname === "/activa/campanas") return "Campañas · DEENEX Activa";
    // elegir qué campaña armar (antes de la regla del detalle por QR, que también es /activa/campanas/<algo>)
    if (pathname === "/activa/campanas/nueva") return "Nueva campaña · Campañas";
    // el calendario de esas campañas (fase 3), también antes de la regla del detalle por QR
    if (pathname === "/activa/campanas/calendario") return "Calendario · Campañas";
    // WhatsApp por QR (líneas): las campañas por archivo, las líneas vinculadas y las casillas de mail de esas campañas.
    if (pathname === "/activa") return "Campañas · WhatsApp QR";
    if (/^\/activa\/campanas\/[^/]+/.test(pathname)) return "Campaña por archivo · WhatsApp QR";
    if (pathname === "/activa/real") return "Campaña nueva · WhatsApp QR";
    if (pathname === "/activa/whatsapp") return "Líneas · WhatsApp QR";
    if (pathname === "/activa/email") return "Casillas de mail · WhatsApp QR";
    if (pathname === "/activa/accesos") return "Accesos · DEENEX Activa";
    if (pathname === "/activa/inicio") return "Inicio · DEENEX Activa";
    // la lista de las campañas con segmentación (la de todas es «/activa/campanas»)
    if (pathname === "/activa/segmentaciones") return "Con segmentación · Campañas";
    if (/^\/activa\/segmentaciones\/[^/]+\/grupos$/.test(pathname)) return "Grupos · Nueva campaña";
    if (/^\/activa\/segmentaciones\/[^/]+\/mensajes$/.test(pathname)) return "Mensajes · Nueva campaña";
    if (/^\/activa\/segmentaciones\/[^/]+\/resultado$/.test(pathname)) return "Resultado · DEENEX Activa";
    if (pathname === "/activa/estadisticas") return "Estadísticas · DEENEX Activa";
    if (pathname === "/activa/segmentaciones/desde-publico") return "Grupos de tu Público · Nueva campaña";
    if (pathname.startsWith("/activa/segmentaciones/")) return "Segmentar · Nueva campaña";
    // Tu marca (el menú: «Tu marca» → Branding, Descuentos y Cupones)
    if (pathname === "/activa/branding") return "Branding · Tu marca";
    if (pathname === "/activa/descuentos") return "Descuentos · Tu marca";
    if (pathname === "/activa/cupones") return "Cupones · Tu marca";
    if (pathname === "/activa/publico/subir") return "Subir base de datos · Público";
    if (pathname.startsWith("/activa/publico/cargas")) return "Contactos con consentimiento · Público";
    if (pathname === "/activa/publico" || pathname.startsWith("/activa/publico/")) return "Público · DEENEX Activa";
    if (pathname === R_EMAILS || pathname.startsWith(`${R_EMAILS}/`)) {
        // La sección del menú a la que pertenece la ruta: el detalle de una campaña o de una persona cuelga de su lista.
        const seccion = SECCIONES_EMAILS.find((s) => (s.end ? pathname === s.path : pathname === s.path || pathname.startsWith(`${s.path}/`)));
        return seccion ? `${seccion.label} · Emails` : "Emails · DEENEX Activa";
    }
    if (pathname === "/objectives") return "Campañas · Deenex AI";
    if (pathname === "/objectives/new/custom") return "Campaña personalizada · Deenex AI";
    if (pathname === "/objectives/new") return "Nueva campaña · Deenex AI";
    if (/^\/objectives\/[^/]+\/setup\/manual/.test(pathname)) return "Configuración manual · Deenex AI";
    if (/^\/objectives\/[^/]+\/setup\/auto/.test(pathname)) return "Configuración automática · Deenex AI";
    if (/^\/objectives\/[^/]+\/setup/.test(pathname)) return "Configurar campaña · Deenex AI";
    if (/^\/objectives\/[^/]+/.test(pathname)) return "Detalle de campaña · Deenex AI";
    if (pathname === "/audience") return "Audiencia · Deenex AI";
    if (pathname === "/segments") return "Segmentos · Deenex AI";
    if (pathname === "/segments/new/ai") return "Segmento con IA · Deenex AI";
    if (pathname === "/segments/new/manual") return "Segmento manual · Deenex AI";
    if (pathname === "/segments/new") return "Nuevo segmento · Deenex AI";
    if (/^\/segments\/[^/]+/.test(pathname)) return "Detalle de segmento · Deenex AI";
    if (pathname === "/notifications/analytics") return "Análisis de notificaciones · Deenex AI";
    if (pathname === "/notifications") return "Notificaciones push · Deenex AI";
    if (/^\/whatsapp\/accounts\/[^/]+\/setup/.test(pathname)) return "Vincular cuenta · Deenex AI";
    if (pathname === "/whatsapp/accounts") return "Cuentas de WhatsApp · Deenex AI";
    if (pathname === "/whatsapp/analytics") return "WhatsApp · Análisis · Deenex AI";
    if (pathname === "/whatsapp/templates/new") return "Nueva plantilla · Deenex AI";
    if (/^\/whatsapp\/templates\/[^/]+/.test(pathname)) return "Editar plantilla · Deenex AI";
    if (pathname === "/whatsapp/templates") return "Plantillas de WhatsApp · Deenex AI";
    if (pathname === "/whatsapp") return "WhatsApp · Campañas · Deenex AI";
    if (pathname === "/brand") return "Manual de tu IA · Deenex AI";
    if (pathname === "/wallet") return "Wallet · Deenex AI";
    if (pathname === "/integrations") return "Integraciones · Deenex AI";
    if (pathname === "/settings") return "Configuración · Deenex AI";
    return NOMBRE_PRODUCTO;
}

const R_CONVERSACIONES = "/activa/oficial/conversaciones";
const esBandeja = (p: string) => p === R_CONVERSACIONES || p.startsWith(`${R_CONVERSACIONES}/`);
const esConversacion = (p: string) => /^\/activa\/oficial\/conversaciones\/[^/]+\/?$/.test(p);

/**
 * Si al pasar de `anterior` a `actual` hay que volver arriba del contenedor. En Conversaciones la lista y el
 * hilo van lado a lado (en la computadora): pasar de una conversación a otra, o abrir una desde la lista, NO
 * sube, porque hacía perder el lugar en la lista. Volver a la lista, o entrar desde otra sección, sí.
 */
export function debeSubirAlCambiar(anterior: string, actual: string): boolean {
    return !(esBandeja(anterior) && esConversacion(actual));
}

const DashboardLayout = () => {
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [inboxOpen, setInboxOpen] = useState(false);
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [assistantPristine, setAssistantPristine] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // El scroll real ocurre en el contenedor `scrollContainerRef`, no en window.
    // React Router v6 no resetea scroll al navegar — hay que hacerlo manual.
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { pathname, search } = useLocation();
    // La ruta anterior, para `debeSubirAlCambiar`. null en el primer render: al montar, arriba.
    const rutaAnterior = useRef<string | null>(null);

    /**
     * DEENEX Activa no muestra la burbuja del assistant: es un botón fijo abajo a la derecha que
     * tapa contenido de las pantallas operativas (el chip de procedencia y las últimas filas del
     * embudo, la paginación de la bitácora). Además el assistant no sabe nada de este módulo.
     */
    const esActiva = pathname.startsWith("/activa");

    /**
     * Inbox y assistant son de la plataforma anterior: sus endpoints no existen en el backend de
     * Activa. Con SOLO_ACTIVA no se cargan en NINGUNA ruta (no sólo en /activa): cualquier otra
     * URL que monte el layout mostraría una campana en 0 que no es verdad y haría polling a un 404.
     */
    const legacy = !SOLO_ACTIVA && !esActiva;

    useEffect(() => {
        // Cada vez que cambia la ruta, volvemos arriba del contenedor scrollable (salvo entre conversaciones).
        const anterior = rutaAnterior.current;
        rutaAnterior.current = pathname;
        if (anterior === null || debeSubirAlCambiar(anterior, pathname)) {
            scrollContainerRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
    }, [pathname]);

    // H07: actualizar el título del documento según la ruta activa. Aparte del scroll: depende también de `search`
    // (Configuración → Saldo) y un filtro en la URL no tiene que subir la pantalla.
    useEffect(() => {
        document.title = getTitleForPath(pathname, search);
    }, [pathname, search]);

    /* ─── Inbox (bell + drawer) ─── */
    const inbox = useInbox(legacy);
    const hasCritical = useMemo(
        () => inbox.items.some((n) => !n.read && n.severity === 'critical'),
        [inbox.items],
    );

    /* Cuando se abre el drawer, refetch para tener data fresca */
    useEffect(() => {
        if (legacy && inboxOpen) inbox.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inboxOpen]);

    /* ─── Assistant chat ─── */
    const assistant = useAssistant(legacy);
    const openAssistant = () => {
        setAssistantOpen(true);
        setAssistantPristine(false); // queda marcado como ya descubierto
    };

    const pantalla = (
        <div className="flex flex-col h-screen w-full bg-slate-50 text-slate-900 font-sans selection:bg-violet-200/30 selection:text-slate-900 overflow-hidden">
            {/* En DEENEX Activa el banner de "modo demo" es FALSO: el módulo habla con el backend
                real y los datos persisten en Mongo. Avisar que "se borran al refrescar" ahí es
                mentirle al usuario sobre sus propios datos. */}
            {!esActiva && <DemoBanner />}
            <div className="flex flex-1 overflow-hidden">
            <Sidebar
                mobileOpen={mobileSidebarOpen}
                onMobileClose={() => setMobileSidebarOpen(false)}
            />

            <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                {/* Mobile top bar — visible only on small screens */}
                <div className="md:hidden flex items-center gap-3 px-4 py-2.5 bg-white/90 backdrop-blur-md border-b border-slate-200/50 shadow-sm flex-shrink-0 z-20">
                    <button
                        onClick={() => setMobileSidebarOpen(true)}
                        aria-label="Abrir menú"
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                    >
                        <Menu size={20} />
                    </button>
                    <img src={logoPrimary} alt={NOMBRE_PRODUCTO} className="h-7 object-contain" />
                    {/* La marca con la que se trabaja, a la vista en el celular: el menú está plegado y las pantallas ya
                        no traen su propio selector (auditoría de diseño del 18/10/2026). Es el mismo del menú. */}
                    {SOLO_ACTIVA && (
                        <div className="ml-auto min-w-0" data-testid="marca-en-barra">
                            <SelectorMarca />
                        </div>
                    )}
                </div>

                {/* Global Background Glow */}
                <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-violet-100/30 to-indigo-50/20 blur-[100px] pointer-events-none z-0"></div>

                <div className="flex-1 flex overflow-hidden z-10">
                    <div
                        ref={scrollContainerRef}
                        /*
                         * El padding inferior existe para que la burbuja del assistant (fija abajo
                         * a la derecha) no tape el final del contenido. En Activa esa burbuja no se
                         * renderiza, así que el padding no protege nada — y encima rompe las barras
                         * ancladas al piso: Chrome calcula el `sticky bottom-0` contra el content-box
                         * del contenedor scrolleable, así que la barra del wizard quedaba flotando
                         * 96px (32px en desktop) sobre una franja gris.
                         */
                        className={`flex-1 overflow-y-auto relative scrollbar-hide ${esActiva ? "" : "pb-24 sm:pb-8"}`}
                    >
                        {/* Si una pantalla tira en el render, el sidebar y el header siguen en pie y
                            navegar a otra sección (resetKey = pathname) limpia el error. */}
                        <ErrorBoundary
                            resetKey={pathname}
                            fallback={(_error, reset) => (
                                <ErrorFallback>
                                    <Link
                                        to={HOME}
                                        onClick={reset}
                                        className="px-4 h-10 inline-flex items-center rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                        Volver al inicio
                                    </Link>
                                </ErrorFallback>
                            )}
                        >
                            <Outlet />
                        </ErrorBoundary>
                    </div>


                </div>

                {selectedEvent && (
                    <LogicDetailModal
                        event={selectedEvent}
                        onClose={() => setSelectedEvent(null)}
                    />
                )}

                {/* ─── Inbox bell flotante ───
                    NO se muestra dentro de DEENEX Activa: es un flujo enfocado (subir datos →
                    configurar → enviar) y la campana ahí sólo estorba —arriba pisaba los botones
                    de acción, abajo tapaba los badges de la lista de campañas—. Fuera de Activa
                    va abajo a la derecha, apilada ENCIMA de la burbuja del assistant. */}
                {legacy && (
                    <InboxBell
                        unreadCount={inbox.unreadCount}
                        hasCritical={hasCritical}
                        onClick={() => setInboxOpen(true)}
                        positionClass="bottom-36 right-4 sm:bottom-[5.5rem]"
                    />
                )}

                {/* ─── Assistant chat bubble (bottom-right, sólo en la plataforma anterior) ─── */}
                {legacy && (
                    <AssistantBubble
                        onClick={openAssistant}
                        pristine={assistantPristine && assistant.messages.length === 0}
                        open={assistantOpen}
                    />
                )}
            </main>
            </div>

            {/* ─── Drawer de notificaciones (overlay sobre todo el layout) ─── */}
            {legacy && (
                <InboxDrawer
                    open={inboxOpen}
                    onClose={() => setInboxOpen(false)}
                    items={inbox.items}
                    unreadCount={inbox.unreadCount}
                    loading={inbox.loading}
                    onMarkRead={inbox.markRead}
                    onMarkAllRead={inbox.markAllRead}
                    onDismiss={inbox.dismiss}
                    onClearAll={inbox.clearAll}
                />
            )}

            {/* ─── Panel del assistant (overlay full-height). Sin burbuja, tampoco panel: si se
                 navega a /activa con el panel abierto, se desmonta. ─── */}
            {legacy && (
                <AssistantPanel
                    open={assistantOpen}
                    onClose={() => setAssistantOpen(false)}
                    messages={assistant.messages}
                    loading={assistant.loading}
                    typing={assistant.typing}
                    error={assistant.error}
                    onSend={assistant.send}
                    onClear={assistant.clear}
                />
            )}

            <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
        </div>
    );

    /*
     * La cuenta de WhatsApp oficial se carga acá y no en `OficialLayout`: las secciones de WhatsApp están en el
     * menú lateral, y el punto de «Configuración» necesita la cuenta desde cualquier pantalla. `OficialLayout`
     * usa este mismo proveedor (no arma otro), así que «Guardar» en Configuración también apaga el punto del menú.
     */
    return SOLO_ACTIVA ? <CuentaOficialProvider>{pantalla}</CuentaOficialProvider> : pantalla;
};

export default DashboardLayout;
